import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { DxLoadPanelComponent, DxLoadPanelModule } from 'devextreme-angular';
import { InitializedEvent } from 'devextreme/ui/load_panel';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { DirectivesModule } from 'src/app/directives/directives/directives.module';
import { ActivatedRouteService } from 'src/app/services/activated-route.service';
import { CounterGroups, LoadingIndicationService } from 'src/app/services/loading-indication.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { UtilsService } from 'src/app/services/utils.service';
import { GroupData } from 'src/app/tools/groups-counter';

@Component({
	selector: 'app-container',
	standalone: true,
	imports: [
		CommonModule,

		DirectivesModule,

		DxLoadPanelModule,
	],
	templateUrl: './container.component.html',
	styleUrl: './container.component.scss'
})
export class ContainerComponent implements OnInit, AfterViewInit, OnDestroy {

	@Input({required: true})
	public id!: number;

	protected contentId: string;

	private _localStorageService: LocalStorageService = inject(LocalStorageService);

	private _routeId?: number;

	/**
	 * The private identifier for the container, computed from the `routeId` and `id`.
	 */
	private _instanceRouteHash: number;
	public get instanceRouteHash(): number {
		return this._instanceRouteHash;
	}

	@Input()
	public showBorders: boolean = true;

	@ViewChild(DxLoadPanelComponent, { static: true })
	public loadPanel: DxLoadPanelComponent;

	@ViewChild('content', { static: true })
	public containerContentRef: HTMLDivElement;

	@Input()
	public loadingHeight: string;

	@Input()
	public contentVisibleDuringLoading: boolean = false;

	protected loading: boolean = false;

	@Input()
	public allowCollapsing: boolean = true;

	private _preventCollapse: boolean = false;

	public preventCollapse(): void {
		this._preventCollapse = true;
	}

	private _collapsed: boolean = false;
	public get collapsed(): boolean {
		return this._collapsed;
	}
	public set collapsed(value: boolean) {
		// this.loading = !value;

		if (this._preventCollapse) {
			this._preventCollapse = false;

			return;
		}

		if (this.allowCollapsing) {
			this._collapsed = value;

			if (this._routeId) {
				this._localStorageService.set<boolean>(
					`perito_route-${this._routeId}_containerCollapsed-${UtilsService.computeRouteHash(this._routeId, this.id)}`,
					this._collapsed
				);
			}
		}
	}

	public onLoadPanelInitialized(evt: InitializedEvent, dxLoadPanelComponent: DxLoadPanelComponent): void {
		this.loadPanel = dxLoadPanelComponent;
	}

	public repaintLoadPanel(): void {
		if (this.loadPanel) {
			this.loadPanel.instance.repaint();
		}
	}

	private _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
	private _loadingIndicationService: LoadingIndicationService = inject(LoadingIndicationService);

	constructor(
		private _activatedRouteService: ActivatedRouteService,
	) {}

	async ngOnInit(): Promise<void> {
		const routeData: Data = await firstValueFrom(this._activatedRoute.data);

		this._routeId = routeData.id;
		if (!this._routeId) {
			// This is probably because parent element exists outside of `router-outlet`, so we use the service to access the `Route` `Id`.
			await firstValueFrom(this._activatedRouteService.routeData$);

			this._routeId = this._activatedRouteService.routeId;
		}
		// console.error(this._routeId, this.id, this._routeId ? UtilsService.computeRouteHash(this._routeId, this.id) : 'Route Id missing.');

		this._instanceRouteHash = UtilsService.computeRouteHash(this._routeId, this.id);

		if (this.allowCollapsing) {
			this._collapsed = this._localStorageService.get<boolean>(`perito_route-${this._routeId}_containerCollapsed-${this._instanceRouteHash}`);
		}

		this.contentId = `#container-content-${this._instanceRouteHash}`;

		this._loadingIndicationService.isLoadingGroupsCounter.getOnIncreaseSubject(CounterGroups.CONTAINER_LOAD).pipe(
			takeUntil(this._destroying$)
		).subscribe(
			(value: Map<string, GroupData>) => {
				if (!value) {
					return;
				}
				const groupData: GroupData = value.get(this._instanceRouteHash.toString());

				if (groupData && groupData.quantity) {
					this.loading = true;
				}
			}
		);

		this._loadingIndicationService.isLoadingGroupsCounter.getOnDecreaseSubject(CounterGroups.CONTAINER_LOAD).pipe(
			takeUntil(this._destroying$)
		).subscribe(
			(value: Map<string, GroupData>) => {
				if (!value) {
					return;
				}

				const groupData: GroupData = value.get(this._instanceRouteHash.toString());

				if (groupData && groupData.quantity === 0 && this.loading) {
					this.loading = false;
				}
			}
		);
	}

	ngAfterViewInit(): void {
		this.repaintLoadPanel();
	}

	/**
	 * Emits a value when the component is about to be destroyed.
	 * Used to automatically unsubscribe from observables to prevent memory leaks.
	 *
	 * - `takeUntil(this._destroying$)` should be used in subscriptions.
	 * - `next()` is called in `ngOnDestroy` to signal unsubscription.
	 * - `complete()` ensures the `Subject` is fully closed, freeing resources.
	 */
	private readonly _destroying$ = new Subject<void>();

	ngOnDestroy() {
		this._destroying$.next(); // Signal all observers to complete.
		this._destroying$.complete(); // Fully close the Subject to avoid memory leaks.
	}
}
